// src/wiring/manifest/omnicineExportDownloader.creator.ts
//
// Creator tier capability (authority):
// - Installs export result downloader (auto-download successful blob exports)
// - Scoped to OmniCine route only
//

import type { CapabilityManifest } from "@/contracts/capabilities/CapabilityManifest";
import { report } from "@/wiring/telemetry/report";

import {
  SYNK_OPS_METRIC_EVENT,
  SYNK_OPS_VIOLATION_EVENT,
  type SynkOpsMetricDetailV1,
  type SynkOpsViolationDetailV1,
} from "@/contracts/ops/OpsEvents";

// Assumes this named export exists (per your earlier plan).
import { installExportResultDownloader } from "@/bridge/omnicine/export/ExportBridge";

const CAP_ID = "omnicine.export.downloader" as const;

function safeDispatch(name: string, detail: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // ignore
  }
}

function emitMetric(name: string, value: number): void {
  const d: SynkOpsMetricDetailV1 = {
    v: 1,
    at: Date.now(),
    kind: "custom",
    name,
    value,
    band: "normal",
    capabilityId: CAP_ID,
  };
  safeDispatch(SYNK_OPS_METRIC_EVENT, d);
}

function emitViolation(reason: string, where = "omnicine.export.downloader"): void {
  const d: SynkOpsViolationDetailV1 = {
    v: 1,
    at: Date.now(),
    severity: "warning",
    channel: "omnicine:export:result",
    where,
    reason,
    schema: "omnicine.export.downloader",
  };
  safeDispatch(SYNK_OPS_VIOLATION_EVENT, d);
}

function isOmniCineRoute(): boolean {
  try {
    return window.location.pathname.startsWith("/omnicine");
  } catch {
    return false;
  }
}

export const OmniCineExportDownloaderCreatorManifest: CapabilityManifest = {
  id: CAP_ID,
  version: "1.0.0",
  safety: "authority",
  tier: "creator",
  requires: [],
  provides: ["omnicine.export.downloader", "omnicine.export.autodownload"],
  billing: { metered: true, unit: "export_minute" },

  install(): () => void {
    // Guard SSR
    if (typeof window === "undefined") return () => {};

    // Route scope: authority-side only.
    if (!isOmniCineRoute()) {
      report("capability.skipped", { id: CAP_ID, reason: "not_omnicine_route" });
      return () => {};
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window as any;

    w.__synk_capabilities = w.__synk_capabilities || {};

    // Idempotency marker
    if (w.__synk_capabilities.omnicineExportDownloaderInstalled) {
      report("capability.warn", { id: CAP_ID, reason: "already_installed" });
      return () => {};
    }
    w.__synk_capabilities.omnicineExportDownloaderInstalled = true;

    let uninstall: (() => void) | null = null;

    try {
      uninstall = installExportResultDownloader({
        dryRun: false,
        shouldDownload: (r: any) => Boolean(r?.ok && r?.blob),
      });

      emitMetric("capability.installed", 1);
      report("capability.installed", { id: CAP_ID, tier: "creator", safety: "authority" });
    } catch (err) {
      emitViolation("install_failed");
      report("capability.install_failed", {
        id: CAP_ID,
        step: "installExportResultDownloader",
        error: String(err),
      });

      // Roll back idempotency marker on failure
      try {
        delete w.__synk_capabilities.omnicineExportDownloaderInstalled;
      } catch {
        // ignore
      }

      return () => {};
    }

    return () => {
      try {
        uninstall?.();
      } catch {
        // ignore
      }

      try {
        if (w.__synk_capabilities) delete w.__synk_capabilities.omnicineExportDownloaderInstalled;
      } catch {
        // ignore
      }

      try {
        report("capability.uninstalled", { id: CAP_ID });
      } catch {
        // ignore
      }
    };
  },
};
